import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Pin, PinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from '@/components/ui/input';
import { MobileNav } from '@/components/mobile-nav';
import { useAuth } from "@/router";
import {
  useSavedAccounts,
  SavedInstagramAccount,
} from "@/hooks/useSavedAccounts";
import ChatProfileImage from "@/components/chat-profile-image";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useAccountQuota } from "@/hooks/use-account-quota";
import { useToast } from "@/hooks/use-toast";

// GSAP 플러그인 등록
gsap.registerPlugin(ScrollTrigger);

// router.tsx에서 import { ChatPage } from '@/pages/chat-page'로 가져오므로 이름 맞춤
export const ChatPage = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  // 저장된 인스타그램 계정 가져오기 - 세션 사용자 ID 전달
  const {
    data: savedAccounts = [],
    isLoading,
  } = useSavedAccounts(session?.user?.id);

  // 계정 쿼터 정보 가져오기
  const { data: quotaInfo } = useAccountQuota();

  // 검색어 상태
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "pinned">("all");
  // 핀된 계정 ID 목록 상태
  const [pinnedAccountIds, setPinnedAccountIds] = useState<string[]>(() => {
    // 초기 상태는 로컬 스토리지에서 로드
    return JSON.parse(localStorage.getItem('pinnedAccounts') || '[]');
  });

  // 애니메이션을 위한 컨테이너 레퍼런스
  const containerRef = useRef<HTMLDivElement>(null);

  // 확장된 인스타그램 계정 타입 (프론트엔드 전용 속성 추가)
  interface ExtendedSavedAccount extends SavedInstagramAccount {
    _isFavorite?: boolean;
    _sentiment?: "positive" | "negative" | "neutral";
    _isLocked?: boolean;
    _isPinned?: boolean;
  }

  // 필터링된 계정 목록
  const filteredAccounts = React.useMemo(() => {
    let accounts = [...savedAccounts] as ExtendedSavedAccount[];

    // 핀 정보 로드
    accounts = accounts.map(account => ({
      ...account,
      _isPinned: pinnedAccountIds.includes(account.id)
    }));

    // 검색어 필터링
    if (searchQuery.trim() !== "") {
      accounts = accounts.filter((account) =>
        account.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (account.profile_data?.fullName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
        (account.display_name?.toLowerCase() || "").includes(searchQuery.toLowerCase())
      );
    }

    // 탭에 따른 필터링
    if (activeTab === "pinned") {
      // 핀된 계정만 필터링
      accounts = accounts.filter((account) => account._isPinned);
    }

    // 계정 쿼터에 따라 잠금 상태 설정
    if (quotaInfo) {
      const { quota } = quotaInfo;

      // 무제한이 아닌 경우에만 잠금 처리
      if (quota !== -1) {
        // 계정을 생성일 기준으로 정렬 (오래된 순)
        const sortedByCreation = [...accounts].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateA - dateB;
        });

        // 쿼터 내의 계정은 잠금 해제, 초과 계정은 잠금 처리
        sortedByCreation.forEach((account, index) => {
          if (index < quota) {
            account._isLocked = false;
          } else {
            account._isLocked = true;
          }
        });

        // 원래 정렬 순서로 다시 정렬
        accounts = accounts.map(account => {
          const matchingAccount = sortedByCreation.find(a => a.id === account.id);
          if (matchingAccount) {
            account._isLocked = matchingAccount._isLocked;
          }
          return account;
        });
      }
    }

    // 핀된 계정을 상단에 표시
    accounts.sort((a, b) => {
      // 먼저 핀 상태로 정렬
      if (a._isPinned && !b._isPinned) return -1;
      if (!a._isPinned && b._isPinned) return 1;

      // 핀 상태가 같으면 최근 채팅 시간으로 정렬
      if (a.last_chatted && b.last_chatted) {
        return new Date(b.last_chatted).getTime() - new Date(a.last_chatted).getTime();
      }

      // 채팅 기록이 없으면 마지막으로 정렬
      if (a.last_chatted && !b.last_chatted) return -1;
      if (!a.last_chatted && b.last_chatted) return 1;

      return 0;
    });

    return accounts;
  }, [savedAccounts, searchQuery, activeTab, quotaInfo, pinnedAccountIds]);

  // 로딩 스켈레톤 컴포넌트
  const ChatSkeletonItem = () => (
    <div className="flex items-center space-x-4 p-2">
      <div className="skeleton h-12 w-12 shrink-0 rounded-full" />
      <div className="space-y-2">
        <div className="skeleton h-4 w-[250px]" />
        <div className="skeleton h-4 w-[200px]" />
      </div>
    </div>
  );

  // 필터링 조건에 맞는 계정이 없을 때 표시
  const EmptySearchResult = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center p-8 text-center"
    >
      <div className="mb-4 rounded-full bg-primary/10 p-3">
        <Search className="h-6 w-6 text-primary" />
      </div>
      <h3 className="mb-2 text-lg font-medium">계정을 찾을 수 없습니다</h3>
      <p className="mb-6 text-sm text-muted-foreground">
        검색어를 변경하거나 다른 필터를 사용해보세요.
      </p>
    </motion.div>
  );

  // GSAP 애니메이션 설정
  useEffect(() => {
    if (containerRef.current && !isLoading && filteredAccounts.length > 0) {
      const listItems = containerRef.current.querySelectorAll(".chat-item");

      // 초기 상태 설정
      gsap.set(listItems, {
        y: 20,
        opacity: 0,
      });

      // 스크롤에 반응하는 애니메이션
      gsap.to(listItems, {
        y: 0,
        opacity: 1,
        stagger: 0.05,
        duration: 0.4,
        ease: "power2.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom-=100",
          toggleActions: "play none none none",
        },
      });

      return () => {
        // 컴포넌트 언마운트 시 스크롤 트리거 정리
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      };
    }
  }, [isLoading, filteredAccounts]);

  // 대화 시작 핸들러 - UX 개선 버전
  const handleStartChat = (account: ExtendedSavedAccount) => {
    // 잠긴 계정인 경우 요금제 페이지로 이동
    if (account._isLocked) {
      toast({
        title: "계정 연결 제한 초과",
        description: `현재 요금제(${quotaInfo?.subscription_tier})에서는 최대 ${quotaInfo?.quota === -1 ? '무제한' : quotaInfo?.quota}개의 계정을 연결할 수 있습니다. 요금제를 업그레이드하세요.`,
        variant: "destructive",
      });
      navigate("/pricing");
      return;
    }

    // 바로 채팅 페이지로 이동하기
    navigate("/search", {
      state: {
        username: account.username,
        direct: "chat", // 이 플래그로 MainRoutes에서 바로 chat 상태로 전환
        profile: account.profile_data || {},
      },
    });
  };

  const { toast } = useToast();

  // 핀 상태 저장/로드 함수
  const togglePinStatus = (account: ExtendedSavedAccount) => {
    // 핀 상태 토글
    const newPinnedStatus = !account._isPinned;

    // 로컬 스토리지에서 핀된 계정 ID 배열을 가져옴
    const newPinnedAccountIds = pinnedAccountIds.includes(account.id) ?
      pinnedAccountIds.filter((id) => id !== account.id) :
      [...pinnedAccountIds, account.id];

    if (newPinnedStatus) {
      toast({
        title: "계정 고정됨",
        description: `@${account.username} 계정이 상단에 고정되었습니다.`,
      });
    } else {
      toast({
        title: "고정 해제됨",
        description: `@${account.username} 계정의 고정이 해제되었습니다.`,
      });
    }

    // 로컬 스토리지에 변경사항 저장
    localStorage.setItem('pinnedAccounts', JSON.stringify(newPinnedAccountIds));

    // React 상태 업데이트 - 이렇게 하면 UI가 즉시 업데이트됨
    setPinnedAccountIds(newPinnedAccountIds);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background pb-[72px]">
      {/* 헤더 */}
      <div className="sticky top-0 z-10 flex flex-col border-b bg-background/95 backdrop-blur-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <motion.h1
              className="text-xl font-bold"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              채팅
            </motion.h1>

            {/* 계정 상태 표시 - 새로 추가된 부분 */}
            {!isLoading && savedAccounts && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <Badge
                  variant="outline"
                  className="ml-2 py-0.5"
                >
                  연결된 계정 {filteredAccounts.length}/{savedAccounts.length}
                </Badge>
              </motion.div>
            )}
          </div>

          {/* 모든 계정 보기 버튼 */}
          {!isLoading && savedAccounts && filteredAccounts.length !== savedAccounts.length && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setActiveTab("all");
              }}
              className="text-xs"
            >
              모든 계정 보기
            </Button>
          )}
        </div>

        {/* 필터링 상태 알림 */}
        {!isLoading && savedAccounts && filteredAccounts.length < savedAccounts.length && searchQuery && (
          <div className="bg-muted/50 px-4 py-2 text-center text-sm text-muted-foreground">
            검색 결과: {filteredAccounts.length}개 계정 표시 중 (전체 {savedAccounts.length}개)
          </div>
        )}

        {/* 탭 선택 시 필터링 상태 알림 */}
        {!isLoading && savedAccounts && filteredAccounts.length < savedAccounts.length && !searchQuery && activeTab !== "all" && (
          <div className="bg-muted/50 px-4 py-2 text-center text-sm text-muted-foreground">
            <span className="font-medium">{activeTab === "pinned" ? "핀된 계정" : ""}</span> 계정만 표시 중입니다
            <Button
              variant="link"
              size="sm"
              onClick={() => setActiveTab("all")}
              className="px-1 py-0 h-auto text-xs font-medium text-primary"
            >
              모든 계정 보기
            </Button>
          </div>
        )}
      </div>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 px-4">
        <Tabs
          defaultValue="all"
          className="w-full"
          onValueChange={(value) => {
            // 타입 안전하게 처리
            if (value === "all" || value === "pinned") {
              setActiveTab(value);
            }
          }}
        >
          <div className="flex items-center justify-between py-4">
            <TabsList className="w-[260px]">
              <TabsTrigger value="all" className="flex-1">
                전체
              </TabsTrigger>
              <TabsTrigger value="pinned" className="flex-1">
                핀된 계정
              </TabsTrigger>
            </TabsList>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="검색..."
                className="w-[160px] pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* 전체 채팅 목록 */}
          <TabsContent value="all" className="mt-0">
            {isLoading ? (
              <div className="space-y-4 py-3">
                {Array(3)
                  .fill(0)
                  .map((_, i) => (
                    <ChatSkeletonItem key={i} />
                  ))}
              </div>
            ) : filteredAccounts.length === 0 ? (
              <EmptySearchResult />
            ) : (
              <motion.div ref={containerRef} className="space-y-1 py-3">
                <AnimatePresence>
                  {filteredAccounts.map((account, index) => (
                    <motion.div
                      key={account.id}
                      className={`relative flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-background/5 ${account._isLocked ? 'opacity-50' : ''}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                        type: "spring",
                        stiffness: 200,
                        damping: 20,
                      }}
                      onClick={() => handleStartChat(account)}
                    >
                      {/* 활성/비활성 상태 표시 */}
                      <div className="relative">
                        <ChatProfileImage
                          username={account.username}
                          src={
                            account.profile_data?.profilePicUrl ||
                            account.supabase_profile_pic_url
                          }
                          size="md"
                        />
                        <div
                          className={`relative rounded-full border-2 ${account.last_chatted && new Date(account.last_chatted).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
                            ? "border-green-500"
                            : "border-transparent"
                            }`}
                          title={account.last_chatted && new Date(account.last_chatted).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
                            ? "최근 1주일 내에 대화함"
                            : ""}
                        />
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <div className="flex items-center gap-1">
                          <p className="truncate font-semibold">
                            {account.display_name ||
                              account.profile_data?.fullName ||
                              account.username}
                          </p>
                          {account.profile_data?.isVerified && (
                            <span className="text-blue-500">✓</span>
                          )}
                          {account._isLocked && (
                            <Badge variant="outline" className="ml-1 text-xs">잠김</Badge>
                          )}
                          {account._isPinned && (
                            <Badge variant="secondary" className="ml-1 text-xs flex items-center gap-1">
                              <Pin className="h-3 w-3" /> 고정됨
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm text-muted-foreground">
                            @{account.username}
                          </p>
                          {/* 마지막 대화 시간 정보 */}
                          <div className="text-xs text-muted-foreground mt-1">
                            {account.last_chatted ? (
                              <span title="마지막 대화 시간">
                                {new Date(account.last_chatted).toLocaleString('ko-KR', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                            ) : (
                              <span className="flex items-center">
                                <span className="text-primary/70">팔로워 {account.profile_data?.followersCount ?
                                  (account.profile_data.followersCount > 1000
                                    ? (account.profile_data.followersCount / 1000).toFixed(1) + 'K'
                                    : account.profile_data.followersCount)
                                  : '0'}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-7 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePinStatus(account);
                            }}
                            title={account._isPinned ? "고정 해제" : "상단에 고정"}
                          >
                            {account._isPinned ? (
                              <PinOff className="h-3.5 w-3.5 text-primary" />
                            ) : (
                              <Pin className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 flex items-center gap-1 px-2 py-0 ${account._isLocked ? 'text-muted-foreground' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartChat(account);
                            }}
                          >
                            <span className="text-xs">{account._isLocked ? '잠김' : '대화'}</span>
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </TabsContent>

          {/* 핀된 계정 탭 */}
          <TabsContent value="pinned" className="mt-0">
            {isLoading ? (
              <div className="space-y-4 py-3">
                {Array(3)
                  .fill(0)
                  .map((_, i) => (
                    <ChatSkeletonItem key={i} />
                  ))}
              </div>
            ) : filteredAccounts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center justify-center p-8 text-center"
              >
                <div className="mb-4 rounded-full bg-primary/10 p-3">
                  <Search className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-medium">
                  핀된 계정이 없습니다
                </h3>
                <p className="mb-6 text-sm text-muted-foreground">
                  인스타그램 계정을 검색하고 핀을 설정하면 여기에 표시됩니다.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab("all")}
                  className="flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  <span>계정 검색하기</span>
                </Button>
              </motion.div>
            ) : (
              <motion.div
                className="space-y-1 py-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {filteredAccounts.map((account, index) => (
                  <motion.div
                    key={account.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05,
                      type: "spring",
                    }}
                    className={`relative flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-background/5 ${account._isLocked ? 'opacity-50' : ''}`}
                    onClick={() => handleStartChat(account)}
                  >
                    {/* 활성/비활성 상태 표시 */}
                    <div className="relative">
                      <ChatProfileImage
                        username={account.username}
                        src={
                          account.profile_data?.profilePicUrl ||
                          account.supabase_profile_pic_url
                        }
                        size="md"
                      />
                      <div
                        className={`relative rounded-full border-2 ${account.last_chatted && new Date(account.last_chatted).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
                          ? "border-green-500"
                          : "border-transparent"
                          }`}
                        title={account.last_chatted && new Date(account.last_chatted).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
                          ? "최근 1주일 내에 대화함"
                          : ""}
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="flex items-center gap-1">
                        <p className="truncate font-semibold">
                          {account.display_name ||
                            account.profile_data?.fullName ||
                            account.username}
                        </p>
                        {account.profile_data?.isVerified && (
                          <span className="text-blue-500">✓</span>
                        )}
                        {account._isLocked && (
                          <Badge variant="outline" className="ml-1 text-xs">잠김</Badge>
                        )}
                        {account._isPinned && (
                          <Badge variant="secondary" className="ml-1 text-xs flex items-center gap-1">
                            <Pin className="h-3 w-3" /> 고정됨
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm text-muted-foreground">
                          @{account.username}
                        </p>
                        {/* 마지막 대화 시간 정보 */}
                        <div className="text-xs text-muted-foreground mt-1">
                          {account.last_chatted ? (
                            <span title="마지막 대화 시간">
                              {new Date(account.last_chatted).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <span className="text-primary/70">팔로워 {account.profile_data?.followersCount ?
                                (account.profile_data.followersCount > 1000
                                  ? (account.profile_data.followersCount / 1000).toFixed(1) + 'K'
                                  : account.profile_data.followersCount)
                                : '0'}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-7 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePinStatus(account);
                          }}
                          title={account._isPinned ? "고정 해제" : "상단에 고정"}
                        >
                          {account._isPinned ? (
                            <PinOff className="h-3.5 w-3.5 text-primary" />
                          ) : (
                            <Pin className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-6 flex items-center gap-1 px-2 py-0 ${account._isLocked ? 'text-muted-foreground' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartChat(account);
                          }}
                        >
                          <span className="text-xs">{account._isLocked ? '잠김' : '대화'}</span>
                          <ArrowRight className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* 하단 네비게이션 */}
      <MobileNav activeItem="chat" />
    </div>
  );
};